import React, {Component} from 'react';
import {injectIntl} from 'react-intl';
import {inject, observer} from 'mobx-react';
import mapValues from 'lodash/mapValues';
import pick from 'lodash/pick';

import {DOMAIN_LOGIN_OPTIONS} from 'enums';
import Field from '../Field';
import {helpArticleURL, emailValidationRegex} from '../../constants';

import {
  LoginForm,
  StyledText,
  StyledIcon,
  EmailWrapperDiv,
  StyledButton,
  ErrorNotification,
  StyledErrorWrapper,
  StyledErrorIcon,
  StyledLink,
  getHeight,
  StyledButtonLink
} from './styles';

import {FormattedMessage} from '../FormattedComponents';
import ForgotWorkspacePasswordDialog from '../ForgotWorkspacePasswordDialog';

import {posthogCapture} from '../../utils/posthog-utils';
import {posthogEvents} from '../../posthog-events';

import messages from './messages';

import ErrorCodes from '../../login-errors';

@inject('store')
@observer
class EmailAndPasswordLogin extends Component {
  componentDidMount() {
    const {
      intl: {formatMessage},
      store: {auth}
    } = this.props;

    const translations = mapValues(pick(messages, ['cantSignInToWorkspace']), message => formatMessage(message));

    auth.setTranslations(translations);
  }

  componentWillUnmount() {
    const {
      store: {authLogin}
    } = this.props;
    const {setErrorResponse} = authLogin;
    setErrorResponse(null);
  }

  onForgotPasswordClick = () => {
    const {store} = this.props;
    const {forgotWorkspacePasswordDialog, app, router} = store;
    const {openDialog} = forgotWorkspacePasswordDialog;
    const {views} = app;

    const isEmailLogin = router.params?.loginType?.toUpperCase() === DOMAIN_LOGIN_OPTIONS.EMAIL;

    if (isEmailLogin) {
      posthogCapture(posthogEvents.selectForgotPassword);
      router.goTo(views.requestResetPassword, {});
    } else {
      openDialog();
    }
  };

  onAllSignInOptionsClick = () => {
    const {store} = this.props;
    const {app, router, authLogin} = store;
    const {setShowErrorMessage} = authLogin;
    const {views} = app;
    router.goTo(views.domainLogin, {}, {redirect: router.queryParams?.redirect});
    setShowErrorMessage(false);
  };

  render() {
    const {
      intl: {formatMessage},
      isMobile,
      store,
      loginOption
    } = this.props;
    const {
      auth: {
        domainLoginForm,
        submitDomainLoginForm,
        domainLoginFormEmailPassword,
        submitDomainLoginFormEmailPassword,
        isOnlyOneLoginOption
      },
      authLogin: {isInputFocused, setIsInputFocused, errorResponse, setErrorResponse, showErrorMessage},
      app: {views},
      router
    } = store;

    if (errorResponse && errorResponse.data === ErrorCodes.LOGIN_OPTION_NOT_ALLOWED && errorResponse.status === 403) {
      store.router.goTo(views.domainLogin, {});
    }

    const isEmailLogin = loginOption
      ? loginOption === DOMAIN_LOGIN_OPTIONS.EMAIL
      : router.params?.loginType?.toUpperCase() === DOMAIN_LOGIN_OPTIONS.EMAIL;

    const form = isEmailLogin ? domainLoginFormEmailPassword : domainLoginForm;
    const {email, password} = form.values();

    const isValidEmail = email ? emailValidationRegex.test(email) : true;

    if (!isValidEmail) {
      setErrorResponse(null);
    }

    const isValidEmailForm = password && email;
    const isDisabled = isEmailLogin ? !isValidEmailForm : !password;
    const onSubmit = isEmailLogin ? submitDomainLoginFormEmailPassword : submitDomainLoginForm;
    const posthogEvent = isEmailLogin ? posthogEvents.attemptEmailSignIn : posthogEvents.attemptWorkspacePasswordSignIn;
    const inputStyles = {Input: getHeight()};

    const passwordPlaceholderLocalizedEntry = isEmailLogin
      ? messages.formField_domainPassword
      : messages.formField_workspacePassword;

    return (
      <LoginForm
        onSubmit={e => {
          e.preventDefault();
          posthogCapture(posthogEvent);
          if (isValidEmail) {
            onSubmit();
          }
        }}
      >
        {isEmailLogin && (
          <EmailWrapperDiv>
            <Field
              placeholder={formatMessage(messages.email)}
              id="email"
              hideError
              styles={inputStyles}
              onChange={e => form.$('email').sync(e.target.value)}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
            />
          </EmailWrapperDiv>
        )}
        {!isValidEmail && !isInputFocused && !errorResponse && showErrorMessage && (
          <StyledErrorWrapper>
            <StyledErrorIcon name={'warning'} />
            <ErrorNotification>{formatMessage(messages.invalidEmail)}</ErrorNotification>
          </StyledErrorWrapper>
        )}
        <Field
          placeholder={formatMessage(passwordPlaceholderLocalizedEntry)}
          field="password"
          id="password"
          type="password"
          hideError
          styles={inputStyles}
          form={form}
          onChange={e => form.$('password').sync(e.target.value)}
          withPasswordToggle
        />
        {!isMobile && (
          <StyledText alignText={'right'}>
            <StyledButtonLink onClick={this.onForgotPasswordClick}>
              {formatMessage(isEmailLogin ? messages.forgotYourPassword : messages.forgotWorkspacePassword)}
            </StyledButtonLink>
          </StyledText>
        )}

        {this.renderError() && (
          <StyledErrorWrapper>
            <StyledErrorIcon name={'warning'} />
            <ErrorNotification>{this.renderError()}</ErrorNotification>
          </StyledErrorWrapper>
        )}

        <StyledButton isMobile={isMobile} type="submit" disabled={isDisabled}>
          {formatMessage(messages.signIn)}
        </StyledButton>

        {isMobile && (
          <StyledText isMobile alignText={'right'}>
            <StyledButtonLink onClick={this.onForgotPasswordClick}>
              {formatMessage(messages.forgotYourPassword)}
            </StyledButtonLink>
          </StyledText>
        )}

        {!isMobile && !isOnlyOneLoginOption && (
          <StyledText>
            <StyledButtonLink onClick={this.onAllSignInOptionsClick}>
              <StyledIcon name={'arrow-left'} />
              {formatMessage(messages.allSignInOptions)}
            </StyledButtonLink>
          </StyledText>
        )}

        <ForgotWorkspacePasswordDialog isMobile={isMobile} />
      </LoginForm>
    );
  }
  renderError() {
    const {store} = this.props;
    const {authLogin, auth, router, app} = store;
    const {views} = app;
    const {errorResponse} = authLogin;
    const {setHasLoginError} = auth;
    const resetPasswordLink = window.location.origin + views.requestResetPassword.path;
    const isEmailLogin = router.params?.loginType?.toUpperCase() === DOMAIN_LOGIN_OPTIONS.EMAIL;

    if (!errorResponse) {
      return null;
    }

    const {status, data} = errorResponse;

    const renderedErrorConfig = [
      {
        shouldRender:
          (status === 401 && data === 'invalid_password' && isEmailLogin) ||
          (status === 403 && data === 'user_disabled' && isEmailLogin),
        message: messages.wrongEmailOrPassword
      },
      {
        shouldRender: status === 500 && data === 'invalid_password' && !isEmailLogin,
        message: messages.wrongWorkspacePassword
      },

      //Not able to login with email 4 times
      {
        shouldRender: status === 401 && data === ErrorCodes.FIFTH_ATTEMPT && isEmailLogin,
        message: messages.wrongEmailOrPasswordFourTimes,
        values: {
          helpArticle: (
            <StyledLink href={helpArticleURL} target="_blank">
              <FormattedMessage {...messages.helpArticle} />
            </StyledLink>
          )
        }
      },
      //Not able to login with email 10 times
      {
        shouldRender: status === 429 && data === ErrorCodes.TOO_MANY_REQUESTS && isEmailLogin,
        message: messages.wrongEmailOrPasswordTenTimes,
        values: {
          passwordResetLink: (
            <StyledLink href={resetPasswordLink} target="_blank">
              <FormattedMessage {...messages.passwordResetLink} />
            </StyledLink>
          )
        }
      },
      //Not able to login because the current change password was wrong 10 times
      {
        shouldRender: status === 429 && data === ErrorCodes.TOO_MANY_CHANGE_REQUESTS && isEmailLogin,
        message: messages.wrongCurrent10thTime,
        values: {
          passwordResetLink: (
            <StyledLink href={resetPasswordLink} target="_blank">
              <FormattedMessage {...messages.passwordResetLink} />
            </StyledLink>
          )
        }
      },
      //If the user has more than 10 failed loggin attempts with workspace password
      {
        shouldRender: status === 401 && data === ErrorCodes.TENTH_ATTEMPT && !isEmailLogin,
        message: messages.tooManyFailedAttempts
      },
      //If the user has more than 31 failed loggin attempts with workspace password
      {
        shouldRender: status === 429 && !isEmailLogin,
        message: messages.tooManyFailedAttemptsBlocked
      },
      //Does not have right to access the workspace while logging in with email and password
      {
        shouldRender: status === 403 && data === ErrorCodes.NOT_ALLOWED_WORKSPACE && isEmailLogin,
        message: messages.doesNotHaveAccess
      }
    ];
    const renderedError = renderedErrorConfig.find(element => element.shouldRender);
    if (renderedError) {
      setHasLoginError(true);
      return <FormattedMessage {...renderedError.message} values={renderedError.values} />;
    } else {
      setHasLoginError(false);
    }
  }
}

export default injectIntl(EmailAndPasswordLogin);
